import {
  fetchRecommendationsSection,
  upgradeAttributes,
  clearCartError,
  showCartError
} from "./chunk-eqgj88e9.js";
import {
  cart
} from "./chunk-9v1sdekd.js";
import {
  readUpgradeOffer,
  readUpgradedLine,
  trackPromotion,
  readRecoTile,
  trackRecoAdd,
  watchCartImpressions,
  confirmRemoval,
  setLineQuantity,
  addRecommendedLine,
  applyUpgrade,
  undoUpgrade,
  disarmLegacyCartForm
} from "./chunk-1sdk6wa7.js";
import {
  drawerAttributes,
  fetchDrawerParts,
  mountDrawer,
  isCartDrawerHost,
  isMobileViewport
} from "./chunk-s8fmrfaa.js";
import {
  subscriptionTypeFor,
  captureFocus
} from "./chunk-fn5j2c34.js";

// src/components/cart-drawer-v2/drawer.ts
var RECOMMENDATIONS_SECTION_ID = "cart-drawer-v2-recommendations";
var RECOMMENDATIONS_SELECTOR = `[${drawerAttributes.recoPerformed}]`;
var DRAWER_OPEN_CLASS = "sidebar--opened";
var readQuantity = (button) => {
  const raw = button.getAttribute(drawerAttributes.quantity);
  if (!raw)
    return null;
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? Math.max(0, parsed) : null;
};
var lineKeyFor = (button) => {
  const line = button.closest(`[${drawerAttributes.lineKey}]`);
  if (!(line instanceof HTMLElement))
    return null;
  return line.getAttribute(drawerAttributes.lineKey);
};
var gateShow = (host, cartUrl) => {
  const show = host.show;
  const hide = host.hide;
  let opener = null;
  host.show = function gatedShow(...args) {
    const forceOpen = this.dataset.cartV2ForceOpen === "true";
    delete this.dataset.cartV2ForceOpen;
    if (!forceOpen && isMobileViewport()) {
      window.location.assign(cartUrl);
      return;
    }
    const active = document.activeElement;
    opener = active instanceof HTMLElement && !this.contains(active) ? active : opener;
    show.apply(this, args);
    this.focus({ preventScroll: true });
  };
  host.hide = function gatedHide(...args) {
    hide.apply(this, args);
    const target = opener;
    opener = null;
    if (target !== null && target.isConnected)
      target.focus({ preventScroll: true });
  };
};

class CartDrawerV2 extends HTMLElement {
  isLoading = false;
  subscription = null;
  loadedProductId = null;
  loadedMarkup = "";
  impressions = null;
  sidebarWatcher = null;
  connectedCallback() {
    this.addEventListener("click", (event) => {
      this.onClick(event);
    });
    this.subscription = cart.subscribe(async () => {
      await this.refresh();
    });
    this.gateMobileOpen();
    this.watchImpressions();
    this.loadRecommendations();
  }
  watchImpressions() {
    const sidebar = this.closest("sidebar-drawer");
    const body = this.querySelector(`[${drawerAttributes.body}]`);
    if (!(sidebar instanceof HTMLElement) || !(body instanceof HTMLElement))
      return;
    const isOpen = () => sidebar.classList.contains(DRAWER_OPEN_CLASS);
    const tracker = watchCartImpressions(this, "cart_drawer", body, isOpen);
    let wasOpen = isOpen();
    const watcher = new MutationObserver(() => {
      const open = isOpen();
      if (open === wasOpen)
        return;
      wasOpen = open;
      if (open)
        tracker.reset();
      else
        tracker.pause();
    });
    watcher.observe(sidebar, { attributes: true, attributeFilter: ["class"] });
    this.impressions = tracker;
    this.sidebarWatcher = watcher;
  }
  gateMobileOpen() {
    const host = this.closest("sidebar-drawer");
    const cartUrl = this.getAttribute(drawerAttributes.cartUrl);
    if (cartUrl === null)
      return;
    if (isCartDrawerHost(host)) {
      gateShow(host, cartUrl);
      return;
    }
    customElements.whenDefined("sidebar-drawer").then(() => {
      if (isCartDrawerHost(host))
        gateShow(host, cartUrl);
    });
  }
  disconnectedCallback() {
    this.subscription?.unsubscribe();
    this.subscription = null;
    this.impressions?.disconnect();
    this.impressions = null;
    this.sidebarWatcher?.disconnect();
    this.sidebarWatcher = null;
  }
  onClick(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement))
      return;
    const upgrade = target.closest(`[${upgradeAttributes.action}]`);
    if (upgrade instanceof HTMLElement) {
      event.preventDefault();
      this.runUpgrade(upgrade);
      return;
    }
    const undo = target.closest(`[${upgradeAttributes.undo}]`);
    if (undo instanceof HTMLElement) {
      event.preventDefault();
      this.runUndo(undo);
      return;
    }
    const add = target.closest(`[${drawerAttributes.add}]`);
    if (add instanceof HTMLElement) {
      event.preventDefault();
      this.addRecommendation(add);
      return;
    }
    const button = target.closest(`[${drawerAttributes.quantity}]`);
    if (!(button instanceof HTMLElement))
      return;
    event.preventDefault();
    this.applyQuantity(button);
  }
  async runUpgrade(button) {
    if (this.isLoading)
      return;
    const lineKey = lineKeyFor(button);
    if (lineKey === null)
      return;
    const offer = readUpgradeOffer(button);
    this.setIsLoading(true);
    clearCartError();
    const result = await applyUpgrade(button, lineKey);
    showCartError(result);
    if (result.ok && offer !== null)
      trackPromotion("select_promotion", "cart_drawer", offer);
    this.setIsLoading(false);
  }
  async runUndo(button) {
    if (this.isLoading)
      return;
    const lineKey = lineKeyFor(button);
    if (lineKey === null)
      return;
    const upgraded = readUpgradedLine(button);
    this.setIsLoading(true);
    clearCartError();
    const result = await undoUpgrade(button, lineKey);
    showCartError(result);
    if (result.ok && upgraded !== null)
      trackPromotion("upgrade_undo", "cart_drawer", upgraded);
    this.setIsLoading(false);
  }
  async applyQuantity(button) {
    if (this.isLoading)
      return;
    const quantity = readQuantity(button);
    const lineKey = lineKeyFor(button);
    if (quantity === null || lineKey === null)
      return;
    if (quantity === 0 && !await confirmRemoval(lineKey))
      return;
    this.setIsLoading(true);
    clearCartError();
    showCartError(await setLineQuantity(lineKey, quantity));
    this.setIsLoading(false);
  }
  setIsLoading(isLoading) {
    this.isLoading = isLoading;
    this.setAttribute(drawerAttributes.isLoading, String(isLoading));
  }
  async addRecommendation(button) {
    if (this.isLoading)
      return;
    const raw = button.getAttribute(drawerAttributes.add);
    const variantId = raw === null ? Number.NaN : Number.parseInt(raw, 10);
    if (!Number.isFinite(variantId))
      return;
    const planRaw = button.getAttribute(drawerAttributes.addPlan);
    const parsedPlan = planRaw === null ? Number.NaN : Number.parseInt(planRaw, 10);
    const sellingPlanId = Number.isFinite(parsedPlan) ? parsedPlan : null;
    const mode = button.getAttribute(drawerAttributes.addMode) === "installments" ? "installments" : "full";
    const properties = {
      _globalId: button.getAttribute(drawerAttributes.addGlobalId) ?? "",
      _discountvalue: button.getAttribute(drawerAttributes.addDiscount) ?? "",
      _selling_plan_name: "",
      _sellingplan: "",
      _ots_installment: "false"
    };
    if (sellingPlanId !== null)
      properties["_subscriptionType"] = subscriptionTypeFor(mode);
    const tile = readRecoTile(button);
    this.setIsLoading(true);
    clearCartError();
    const result = await addRecommendedLine({ variantId, sellingPlanId, properties }, tile?.listId ?? null);
    showCartError(result);
    if (result.ok && tile !== null)
      trackRecoAdd(tile);
    this.setIsLoading(false);
  }
  async refresh() {
    const anchor = captureFocus(this);
    const parts = await fetchDrawerParts();
    if (parts === null)
      return;
    mountDrawer(this, parts);
    this.restoreRecommendations();
    anchor.restore();
    await this.loadRecommendations();
    anchor.restore();
  }
  recoKey(host) {
    const productId = host.getAttribute(drawerAttributes.recoProduct) ?? "";
    return `${productId}|${host.getAttribute(drawerAttributes.recoOption) ?? ""}`;
  }
  restoreRecommendations() {
    if (this.loadedMarkup === "")
      return;
    const host = this.querySelector("cart-drawer-v2-recommendations");
    if (!(host instanceof HTMLElement))
      return;
    if (this.recoKey(host) !== this.loadedProductId)
      return;
    host.innerHTML = this.loadedMarkup;
  }
  async loadRecommendations() {
    const host = this.querySelector("cart-drawer-v2-recommendations");
    if (!(host instanceof HTMLElement))
      return;
    const productId = host.getAttribute(drawerAttributes.recoProduct) ?? "";
    if (productId === "") {
      host.innerHTML = "";
      this.loadedProductId = null;
      this.loadedMarkup = "";
      return;
    }
    const key = this.recoKey(host);
    if (key === this.loadedProductId && this.loadedMarkup !== "")
      return;
    const markup = await fetchRecommendationsSection(RECOMMENDATIONS_SECTION_ID, productId, host.getAttribute(drawerAttributes.recoLimit) ?? "3", RECOMMENDATIONS_SELECTOR);
    host.innerHTML = markup ?? "";
    this.loadedProductId = key;
    this.loadedMarkup = markup ?? "";
  }
}
var registerCartDrawerV2 = () => {
  if (customElements.get("cart-drawer-v2") === undefined) {
    customElements.define("cart-drawer-v2", CartDrawerV2);
  }
};

// src/components/cart-drawer-v2/index.ts
registerCartDrawerV2();
disarmLegacyCartForm(drawerAttributes.legacy);
